// Onboarding.jsx
// Multi-step onboarding questionnaire shown to new users after signup.
// Collects 10 answers that seed central memory for every agent session.
// One question per screen with a progress bar and smooth fade transition.
// All answers are saved to Supabase via PATCH /memory before redirecting to /dashboard.

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateMemory } from '../api';

const QUESTIONS = [
  {
    id: 'business_name',
    question: "What's your business called?",
    subtext: "Or what are you thinking of calling it? You can always change it later.",
    type: 'text',
    placeholder: 'e.g. Rocket-Fuel, Sunrise Coffee, BlueSky Software…',
  },
  {
    id: 'business_type',
    question: 'What kind of business is it?',
    type: 'choice',
    options: [
      'Software / App',
      'Physical Product',
      'Service Business',
      'Restaurant / Food',
      'Retail / E-commerce',
      'Other',
    ],
  },
  {
    id: 'stage',
    question: 'How would you describe where you are right now?',
    type: 'choice',
    options: [
      'Just an idea — nothing built yet',
      'I have a product but no customers',
      'I have some customers but no real structure',
      "I'm growing and need to get organized",
    ],
  },
  {
    id: 'target_customer',
    question: 'Who is your target customer?',
    type: 'text',
    placeholder: 'e.g. Small business owners who need affordable accounting software',
  },
  {
    id: 'problem_statement',
    question: 'What problem are you solving for them?',
    type: 'textarea',
    placeholder: 'Describe the problem your customer faces that your business will solve…',
  },
  {
    id: 'revenue_model',
    question: 'How are you (or planning to) make money?',
    type: 'choice',
    options: [
      'Subscription (monthly/yearly)',
      'One-time purchase',
      'Service fees / hourly',
      'Commission or marketplace',
      'Not sure yet',
    ],
  },
  {
    id: 'primary_challenge',
    question: "What's your biggest challenge right now?",
    type: 'choice',
    options: [
      'Turning my idea into something concrete',
      'Finding my first customer',
      'Understanding the financials',
      'Legal and business structure',
      'Growing too fast to keep up',
      'Something else',
    ],
  },
  {
    id: 'time_commitment',
    question: 'How much time can you dedicate to this per week?',
    type: 'choice',
    options: [
      'A few hours (side project)',
      'Part time (10–20 hours)',
      'Full time (40+ hours)',
    ],
  },
  {
    id: 'budget_range',
    question: 'Do you have a rough budget to invest in the business?',
    type: 'choice',
    options: [
      'Bootstrapped — as little as possible',
      'Under $1,000',
      '$1,000 – $10,000',
      '$10,000+',
    ],
  },
  {
    id: 'excitement',
    question: "What's one thing you're most excited about?",
    type: 'textarea',
    placeholder: 'Tell us what gets you fired up about this business…',
  },
];

const TOTAL = QUESTIONS.length;

export default function Onboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [error, setError] = useState('');
  const [visible, setVisible] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  const question = QUESTIONS[currentStep];
  const isLastStep = currentStep === TOTAL - 1;
  const progress = ((currentStep + 1) / TOTAL) * 100;

  // Focus text input whenever the step changes.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setError('');
  }, [currentStep]);

  // Fade out → update step → fade in.
  function transition(callback) {
    setVisible(false);
    setTimeout(() => {
      callback();
      setVisible(true);
    }, 180);
  }

  function handleNext() {
    const currentValue = answers[question.id] || '';

    if (question.type === 'text' || question.type === 'textarea') {
      if (!currentValue.trim()) {
        setError('Please fill this in before moving on.');
        return;
      }
    } else if (!currentValue) {
      setError('Please pick an option to continue.');
      return;
    }

    setError('');

    if (isLastStep) {
      submitAnswers(answers);
    } else {
      transition(() => setCurrentStep((step) => step + 1));
    }
  }

  function handleBack() {
    if (currentStep === 0) return;
    transition(() => setCurrentStep((step) => step - 1));
  }

  // Single handler for text/textarea inputs — writes directly into answers.
  function handleValueChange(value) {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    setError('');
  }

  // Single handler for multiple-choice selections.
  function handleChoiceSelect(option) {
    setAnswers((prev) => ({ ...prev, [question.id]: option }));
    setError('');
  }

  async function submitAnswers(finalAnswers) {
    setIsSubmitting(true);
    try {
      await updateMemory({ updates: finalAnswers });
      navigate('/dashboard');
    } catch {
      setIsSubmitting(false);
      setError('Something went wrong saving your answers. Please try again.');
    }
  }

  // Loading screen while PATCH /memory is in flight.
  if (isSubmitting) {
    return (
      <div style={styles.page}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingWrap}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Building your dashboard…</p>
        </div>
      </div>
    );
  }

  const cardStyle = {
    ...styles.card,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(10px)',
    transition: 'opacity 0.18s ease, transform 0.18s ease',
  };

  return (
    <div style={styles.page}>
      {/* Fixed orange progress bar across the top of the viewport */}
      <div style={styles.progressTrack}>
        <div style={{ ...styles.progressFill, width: `${progress}%` }} />
      </div>

      <p style={styles.stepLabel}>
        Question {currentStep + 1} of {TOTAL}
      </p>

      <div style={cardStyle}>
        <h2 style={styles.questionText}>{question.question}</h2>
        {question.subtext && <p style={styles.subtext}>{question.subtext}</p>}

        {question.type === 'text' && (
          <input
            ref={inputRef}
            type="text"
            style={styles.textInput}
            value={answers[question.id] || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={question.placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNext(); }}
          />
        )}

        {question.type === 'textarea' && (
          <textarea
            ref={inputRef}
            style={styles.textArea}
            value={answers[question.id] || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={question.placeholder}
            rows={4}
          />
        )}

        {question.type === 'choice' && (
          <div style={styles.optionsGrid}>
            {question.options.map((option) => {
              const isSelected = answers[question.id] === option;
              return (
                <button
                  key={option}
                  style={isSelected
                    ? { ...styles.optionCard, ...styles.optionCardActive }
                    : styles.optionCard}
                  onClick={() => handleChoiceSelect(option)}
                >
                  {option}
                </button>
              );
            })}
          </div>
        )}

        {error && <p style={styles.errorText}>{error}</p>}

        <div style={styles.navRow}>
          {currentStep > 0 && (
            <button style={styles.backBtn} onClick={handleBack}>
              Back
            </button>
          )}
          <button
            style={{
              ...styles.nextBtn,
              marginLeft: currentStep > 0 ? 'auto' : undefined,
            }}
            onClick={handleNext}
          >
            {isLastStep ? "Let's go!" : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1A1A2E',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    padding: '24px',
    paddingTop: '48px',
    boxSizing: 'border-box',
  },

  // ── Progress bar ──────────────────────────────────────────────────────────
  progressTrack: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: 'rgba(255,255,255,0.07)',
    zIndex: 100,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#FF6B35',
    borderRadius: '0 2px 2px 0',
    transition: 'width 0.35s ease',
  },

  stepLabel: {
    color: 'rgba(255,255,255,0.3)',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    marginBottom: '20px',
    marginTop: 0,
  },

  // ── Question card ─────────────────────────────────────────────────────────
  card: {
    backgroundColor: '#16213E',
    borderRadius: '20px',
    padding: '40px 36px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
    border: '1px solid rgba(255,255,255,0.06)',
    boxSizing: 'border-box',
  },
  questionText: {
    margin: '0 0 6px',
    fontSize: '22px',
    fontWeight: '700',
    color: '#FFFFFF',
    lineHeight: '1.35',
    letterSpacing: '-0.3px',
  },
  subtext: {
    margin: '6px 0 0',
    color: 'rgba(255,255,255,0.4)',
    fontSize: '14px',
    lineHeight: '1.6',
  },

  // ── Text inputs ───────────────────────────────────────────────────────────
  textInput: {
    display: 'block',
    width: '100%',
    padding: '14px 16px',
    marginTop: '24px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
  },
  textArea: {
    display: 'block',
    width: '100%',
    padding: '14px 16px',
    marginTop: '24px',
    backgroundColor: 'rgba(255,255,255,0.05)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    fontSize: '16px',
    color: '#fff',
    boxSizing: 'border-box',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    minHeight: '110px',
  },

  // ── Multiple choice ───────────────────────────────────────────────────────
  optionsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '24px',
  },
  optionCard: {
    padding: '14px 18px',
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: '10px',
    fontSize: '15px',
    color: 'rgba(255,255,255,0.75)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
    lineHeight: '1.4',
    transition: 'border-color 0.12s, background-color 0.12s, color 0.12s',
  },
  optionCardActive: {
    backgroundColor: 'rgba(255,107,53,0.14)',
    borderColor: '#FF6B35',
    color: '#FF6B35',
    fontWeight: '600',
  },

  // ── Error message ─────────────────────────────────────────────────────────
  errorText: {
    margin: '14px 0 0',
    color: '#FCA5A5',
    fontSize: '13px',
  },

  // ── Navigation row ────────────────────────────────────────────────────────
  navRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '32px',
    gap: '12px',
  },
  backBtn: {
    padding: '12px 20px',
    backgroundColor: 'transparent',
    border: '1.5px solid rgba(255,255,255,0.12)',
    borderRadius: '8px',
    color: 'rgba(255,255,255,0.45)',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  nextBtn: {
    padding: '14px 28px',
    backgroundColor: '#FF6B35',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },

  // ── Loading screen ────────────────────────────────────────────────────────
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  spinner: {
    width: '44px',
    height: '44px',
    border: '3px solid rgba(255,107,53,0.2)',
    borderTopColor: '#FF6B35',
    borderRadius: '50%',
    animation: 'spin 0.75s linear infinite',
  },
  loadingText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: '18px',
    fontWeight: '500',
    margin: 0,
  },
};
